import { Fragment, useEffect, useImperativeHandle, useRef } from 'react';
import { type Ref } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';

import { CommentForm } from 'hb-react/frontoffice/apps/CommentsV2/components/CommentForm';
import { LoadMore } from 'hb-react/frontoffice/apps/CommentsV2/components/LoadMore';
import { CommentWithReplies } from 'hb-react/frontoffice/apps/CommentsV2/components/v3/CommentWithReplies';
import { useCommentsContext } from 'hb-react/frontoffice/apps/CommentsV2/contexts/CommentsContext';
import { useComments } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useComments';
import { useCommentsQueryHandlers } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useCommentsQueryHandlers';
import { useDeferredFocus } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useDeferredFocus';
import { useRemoveFocusedCommentsSearchParams } from 'hb-react/frontoffice/apps/CommentsV2/hooks/useFocusedCommentSearchParams';
import {
  type Comment,
  type CommentableType,
} from 'hb-react/frontoffice/apps/CommentsV2/types';
import { getQueryKey } from 'hb-react/frontoffice/apps/CommentsV2/utils/getQueryKey';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { type QuillEditorV3Ref } from 'hb-react/shared/components/DesignSystem/QuillEditor/types';

export interface CommentsListImperativeHandle {
  focusCommentForm: () => void;
}

interface CommentsListProps {
  autoFocus?: boolean;
  commentableId: number;
  commentableType: CommentableType;
  commentsRef?: Ref<CommentsListImperativeHandle>;
  onCommentFormBlur?: () => void;
  onCommentsUpdate?: (comments: {
    comments: Array<Comment>;
    totalComments: number;
  }) => void;
  onNewCommentCallback?: () => void;
}

const CommentsList = ({
  commentsRef,
  autoFocus = false,
  commentableId,
  commentableType,
  onCommentsUpdate,
  onNewCommentCallback,
  onCommentFormBlur,
}: CommentsListProps) => {
  const { currentUser } = useDefaultAppContexts();
  const removeCommentsSearchParams = useRemoveFocusedCommentsSearchParams();

  const {
    canDisplayEmojiPicker,
    focusedCommentId,
    focusedReplyId,
    isOpenInModal,
    sortBy,
  } = useCommentsContext();

  const quillRef = useRef<QuillEditorV3Ref>(null);

  const queryKey = getQueryKey({
    commentableId,
    commentableType,
    sortBy,
    isOpenInModal,
    focusedCommentId,
    focusedReplyId,
  });

  const {
    data,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isLoading,
  } = useComments({
    queryKey,
    params: {
      commentableId,
      commentableType,
      sortBy,
      focusedCommentId,
    },
    onFocusedCommentNotFound: () => removeCommentsSearchParams(),
  });

  const {
    handleNewComment,
    handleUpdateComment,
    handleDeleteComment,
    handleToggleLike,
    handleVote,
  } = useCommentsQueryHandlers(queryKey);

  const handleCreateNewComment = ({ newComment }: { newComment: Comment }) => {
    handleNewComment({ newComment });
    onNewCommentCallback?.();
  };

  const focusCommentForm = useDeferredFocus(quillRef);

  useImperativeHandle(
    commentsRef,
    () => ({
      focusCommentForm,
    }),
    [focusCommentForm],
  );

  useEffect(() => {
    if (data?.pages?.length && onCommentsUpdate) {
      const lastPage = data.pages[data.pages.length - 1];

      onCommentsUpdate({
        comments: data.pages.map((page) => page.data).flat(),
        totalComments:
          lastPage.total_count_with_replies ?? lastPage.total_count ?? 0,
      });
    }
  }, [onCommentsUpdate, data?.pages]);

  if (isLoading) {
    return (
      <Box py={6}>
        <Loader size={30} />
      </Box>
    );
  }

  return (
    <Box flexDirection="column" mt={5} pb={4}>
      <CommentForm
        // @ts-expect-error untyped component
        autoFocus={autoFocus}
        canDisplayEmojiPicker={canDisplayEmojiPicker}
        commentableId={commentableId}
        commentableType={commentableType}
        handleOnFocus={focusCommentForm}
        onBlur={onCommentFormBlur}
        onComment={handleCreateNewComment}
        ref={quillRef}
        user={currentUser}
      />

      {hasPreviousPage ? (
        <Box justifyContent="flex-end" mb={4}>
          <LoadMore
            isFetching={isFetchingPreviousPage}
            loadMore={fetchPreviousPage}
            loadingText={s__('Comments|Loading...')}
            linkText={s__('Comments|See more comments')}
          />
        </Box>
      ) : null}

      {data?.pages.map((page) => (
        <Fragment key={`${page.start_cursor}-${page.end_cursor}`}>
          {page.data.map((comment) => (
            <CommentWithReplies
              key={comment.id}
              canDisplayEmojiPicker={canDisplayEmojiPicker}
              comment={comment}
              currentUser={currentUser}
              onDeleteComment={handleDeleteComment}
              onToggleLike={handleToggleLike}
              onUpdateComment={handleUpdateComment}
              onVote={handleVote}
            />
          ))}
        </Fragment>
      ))}

      {hasNextPage ? (
        <Box justifyContent="flex-end">
          <LoadMore
            isFetching={isFetchingNextPage}
            loadMore={fetchNextPage}
            loadingText={s__('Comments|Loading...')}
            linkText={s__('Comments|See more comments')}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default CommentsList;
